/**
 * RoadsLayer.tsx — high-traffic roads overlay (T-061)
 *
 * A line overlay of the NHS Interstate/US Route backbone plus any arterial carrying >= 10k
 * AADT (GET /api/roads/bbox, backed by the pre-simplified `roads` table). The server sheds
 * the low-traffic tail by zoom tier (minTierForZoom), so a far extent returns only the
 * highest-traffic roads and the national payload stays bounded.
 *
 * Styling: HUE by route type keeps the legacy palette as the base — Interstate #E67E22
 * (orange), US Route #8E44AD (purple), other arterials in neutral slate — while STRENGTH
 * (width + opacity) scales with the AADT tier, so heavier-traffic roads read stronger.
 *
 * Lifecycle mirrors CountyLayer (T-012 W2 pattern): the source + layer are added ONCE and
 * kept mounted; a `visible` prop toggles layout visibility rather than tearing the source
 * down. The bbox fetch (debounced, retried, abortable — BEAD-0030 patterns) only runs
 * while visible.
 *
 * NATIONAL-SCALE GATE (T-061): the layer neither fetches nor renders below ROADS_MIN_ZOOM.
 * At national/regional zoom the choropleth is the story, and fetching every Interstate in a
 * multi-state extent is exactly the heavy-payload burst that caused the BEAD-0030 503s. The
 * gate is enforced twice — the fetch effect bails below the threshold, and the MapLibre
 * layer carries `minzoom` so any already-loaded geometry also stops rendering when zoomed out.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'

export interface RoadsLayerProps {
  /** Current viewport bbox as [west, south, east, north]. null before map loads. */
  bbox: [number, number, number, number] | null
  /** Current map zoom — used for the national-scale fetch/render gate. */
  zoom: number
  /** Show/hide the layer (driven by the LayersControl toggle). Default off. */
  visible?: boolean
}

const SOURCE_ID = 'roads'
const LAYER_ID = 'roads-line'

// ─── T-061 national-scale gate ─────────────────────────────────────────────────
// No fetch and no render below this zoom. z6 ≈ a multi-state regional extent; below it the
// county choropleth is the whole picture and an all-Interstates fetch is the BEAD-0030
// failure mode. Matches the county tier's low-zoom home (COUNTY_MAX_ZOOM=9) — roads become
// useful as you zoom toward a specific corridor, not at national overview.
export const ROADS_MIN_ZOOM = 6

// ─── BEAD-0030 burst-resilience tuning (identical to CountyLayer) ──────────────
const DEBOUNCE_MS = 200
const MAX_RETRIES = 2
const BACKOFF_BASE_MS = 250

/** Resolves after `ms`, or rejects with an AbortError if `signal` aborts first. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
    const t = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

/**
 * Fetch the roads FeatureCollection for a bbox, retrying transient 5xx responses with a
 * linear backoff. A 4xx is not retried — only server saturation (5xx) is transient.
 */
async function fetchRoadsInBbox(
  bbox: [number, number, number, number],
  zoom: number,
  signal: AbortSignal,
): Promise<GeoJSON.FeatureCollection> {
  const [west, south, east, north] = bbox
  const params = new URLSearchParams({
    south: String(south),
    west: String(west),
    north: String(north),
    east: String(east),
    // Server sheds the low-traffic tail by zoom (minTierForZoom) so a far extent stays small.
    zoom: String(zoom),
  })
  for (let attempt = 0; ; attempt++) {
    const r = await authedFetch(`/api/roads/bbox?${params}`, { signal })
    if (r.ok) return r.json() as Promise<GeoJSON.FeatureCollection>
    if (r.status >= 500 && attempt < MAX_RETRIES) {
      await sleep(BACKOFF_BASE_MS * (attempt + 1), signal)
      continue
    }
    throw new Error(`roads/bbox ${r.status}`)
  }
}

export function RoadsLayer({ bbox, zoom, visible = false }: RoadsLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)

  // One-time: add source + line layer + hover tooltip.
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    // Route-type styling — legacy parity (Map_Rendering_Reference §11). The `minzoom` here
    // is the render half of the national-scale gate: even if the source still holds geometry
    // from a prior extent, nothing draws below ROADS_MIN_ZOOM.
    map.addLayer({
      id: LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      minzoom: ROADS_MIN_ZOOM,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // HUE by route type — keep the legacy palette (Interstate orange / US Route purple),
        // arterials in a neutral slate so the federal backbone still reads as the base palette.
        'line-color': [
          'match',
          ['get', 'route_type'],
          'Interstate',
          '#E67E22',
          'US Route',
          '#8E44AD',
          /* Arterial / other */ '#64748b',
        ],
        // STRENGTH by AADT tier — heavier traffic draws thicker + more opaque (T-061 amendment).
        'line-width': ['match', ['get', 'aadt_tier'], 3, 3.0, 2, 2.0, /* tier 1 / other */ 1.2],
        'line-opacity': ['match', ['get', 'aadt_tier'], 3, 0.85, 2, 0.7, /* tier 1 / other */ 0.55],
      },
    })

    const hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 8,
    })

    const handleEnter = (e: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const f = e.features?.[0]
      if (!f?.properties) return
      const p = f.properties as Record<string, unknown>
      const routeType = String(p['route_type'] ?? 'Road')
      const name = String(p['name'] ?? '')
      const aadtRaw = Number(p['aadt'])
      const aadt =
        Number.isFinite(aadtRaw) && aadtRaw > 0 ? `${aadtRaw.toLocaleString()} AADT` : 'AADT n/a'
      hoverPopup
        .setLngLat(e.lngLat)
        .setHTML(
          `<div style="font-family:Inter,sans-serif;font-size:12px;padding:2px 0">
            <span style="font-weight:700;color:var(--foreground)">${name || routeType}</span><br/>
            <span style="color:var(--muted-foreground);font-size:10px">${routeType} · ${aadt}</span>
          </div>`,
        )
        .addTo(map)
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
      hoverPopup.remove()
    }

    map.on('mouseenter', LAYER_ID, handleEnter)
    map.on('mouseleave', LAYER_ID, handleLeave)

    return () => {
      initializedRef.current = false
      hoverPopup.remove()
      map.off('mouseenter', LAYER_ID, handleEnter)
      map.off('mouseleave', LAYER_ID, handleLeave)
      // BEAD-0037: skip layer/source teardown if MapView's map.remove() already ran on
      // sign-out (getLayer would throw on the dropped style and blank the app).
      if (isMapAlive(map)) {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
      }
    }
  }, [map])

  // Visibility toggle — show/hide the line layer without tearing down the source.
  useEffect(() => {
    if (!map || !initializedRef.current) return
    const vis = visible ? 'visible' : 'none'
    if (map.getLayer(LAYER_ID)) map.setLayoutProperty(LAYER_ID, 'visibility', vis)
  }, [map, visible])

  // Fetch + update source on bbox change — ONLY while visible AND at/above ROADS_MIN_ZOOM
  // (the national-scale fetch gate). Below the threshold we skip the request entirely so a
  // zoomed-out extent never pulls an all-Interstates payload. Debounced + retried + abortable
  // (BEAD-0030). bbox and zoom both change on moveend; the AbortController cancels any stale
  // in-flight request so the pair updating together costs at most one live fetch.
  useEffect(() => {
    if (!map || !bbox || !visible || zoom < ROADS_MIN_ZOOM) return

    const controller = new AbortController()
    const { signal } = controller

    const timer = setTimeout(() => {
      fetchRoadsInBbox(bbox, zoom, signal)
        .then((data) => {
          if (signal.aborted) return
          const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
          source?.setData(data)
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name !== 'AbortError') console.error(err)
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [map, bbox, visible, zoom])

  return null
}
